import { Component, OnDestroy, OnInit, HostListener, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, ValidateAndNavigateEvent, ProgressBarEvent, SkipToMainContentEvent } from '../service/events';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { AuthService } from '../service/auth.service';
import { MatRadioChange } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AFDisclosure, KeyVal, RadioGroupButtonModel, AF_CONSTANTS } from '../model/af.model';
import { ConsentService } from '../service/consent.service';
import { DatePipe, KeyValue } from '@angular/common';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
  selector: 'app-acknowledgement',
  templateUrl: './acknowledgement.component.html',
  styleUrls: ['./acknowledgement.component.scss'],
  standalone: false
})
export class AcknowledgementComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  afDisclosureInfo: AFDisclosure;
  ackData: UntypedFormGroup;
  signatureImg: string; //for @Input wet-signature
  date: any;
  termDate: any;
  ackRelatives: string;
  ackPendingInvest: string;
  ackObaodDirector: string;
  ackObaodCompensation: string;
  ackEthnicity: string;
  ackTerms: string;
  ackTerms16: string;
  ackRevolvingDoor: string;
  ackRevolvingDoor1: string;
  ackRevolvingDoor1A: string;
  ackRevolvingDoor1B: string;
  ackRevolvingDoor2: string;
  ackRevolvingDoor2A: string;
  authLanguageLookup: string;
  raceLookup: KeyVal[];
  hasBeenSubmitted: boolean = false;
  relativeListChange: boolean = false;
  radioButtonGroupData: RadioGroupButtonModel[] = [];
  check: boolean = false;
  checkTwo: boolean = false;
  sigCheckValue: boolean = false;
  termsCheck: boolean = false;

  showRadioRelatives: boolean = false;
  showRadioInvestigation: boolean = false;
  showRadioOutsideBusinessQuestion1: boolean = false;
  showRadioEmployedCompensated: boolean = false;
  showRadioEmployeeOfEntity: boolean = false;
  showRadioContactWithEntity: boolean = false;

  @ViewChild("pdfButton") pdfButton: ElementRef;

  constructor(private readonly eventBus: EventService, private readonly _router: Router, private readonly fb: UntypedFormBuilder, private readonly userService: AuthService, private readonly datepipe: DatePipe,
    private readonly consentService: ConsentService, private readonly _snackBar: MatSnackBar, private readonly customValidator: CustomValidatorsService) {
    super(eventBus, _router);
  }

  get output() { return this.ackData.get('output') }
  get legalRequirementsMet() { return this.ackData.get('legalRequirementsMet') }
  get applicantSignedName() { return this.ackData.get('applicantSignedName') }
  get discTermsCheck() { return this.ackData.get('discTermsCheck') }
  //Radio Buttons
  get relatives() { return this.ackData.get('relatives') }
  get investigation() { return this.ackData.get('investigation') }
  get employeeOfEntity() { return this.ackData.get('employeeOfEntity') }
  get contactWithEntity() { return this.ackData.get('contactWithEntity') }
  //Form Array
  get relativeList() { return this.ackData.get('relativeList') as UntypedFormArray; }
  get bookmarkUrl() { return this.ackData.get('bookmarkUrl') }

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    this.ackData.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.ackData);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/acknowledgement");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

    this.handleZoom();
  }

  createGroup() {
    this.ackData = this.fb.group({
      discTermsCheck: ['', Validators.requiredTrue],
      output: ['', Validators.required],
      legalRequirementsMet: ['', Validators.requiredTrue],
      applicantSignedName: ['', [Validators.required, Validators.maxLength(250), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      //Flag Fields
      relatives: [''],
      relativeList: new UntypedFormArray([]),
      investigation: [''],
      investigationComments: ['', [Validators.maxLength(1000)]],
      outsideBusinessQuestion1: [''],
      employedCompensated: [''],
      race: [''],
      employeeOfEntity: [''],
      contactWithEntity: [''],
      positionInEntity: [''],
      nameOfEntity: [''],
      bookmarkUrl: [''],
      entityDetails: ['', [Validators.maxLength(1000)]]
    });
  }

  subscribeEvents() {
    this.consentService.getAfDisclosure().subscribe((data: AFDisclosure) => {
      this.afDisclosureInfo = data;
      this.ackData.patchValue(data);
      this.setLanguage();
      this.authLanguageLookup = data.authLanguageLookup;
      this.raceLookup = data.raceLookup;
      this.signatureImg = data.output;
      this.date = data.legalRequirementCheckDate;
      this.termDate = data.discTermsCheckDate;


      /*
      radio button meta data
      */
      //relatives
      let radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioRelatives";
      if (data.relativeFlag != "-1") {
        radioGroupMetaData.groupValue = data.relatives;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);
      //investigation
      radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioInvestigation";
      if (data.pendingInvestigationFlag != "-1") {
        radioGroupMetaData.groupValue = data.investigation;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);
      //outsideBusinessQuestion1
      radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioOutsideBusinessQuestion1";
      if (data.directorFlag != "-1") {
        radioGroupMetaData.groupValue = data.outsideBusinessQuestion1;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);
      //employedCompensated
      radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioEmployedCompensated";
      if (data.directorFlag != "-1") {
        radioGroupMetaData.groupValue = data.employedCompensated;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);
      //employeeOfEntity
      radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioEmployeeOfEntity";
      if (data.revolvingDoorFlag != "-1") {
        radioGroupMetaData.groupValue = data.employeeOfEntity;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);
      //contactWithEntity
      radioGroupMetaData = new RadioGroupButtonModel();
      radioGroupMetaData.groupName = "radioContactWithEntity";
      if (data.revolvingDoorFlag != "-1") {
        radioGroupMetaData.groupValue = data.contactWithEntity;
      }
      this.radioButtonGroupData.push(radioGroupMetaData);

      for (let relative of this.afDisclosureInfo.relativeList) {
        let fg: UntypedFormGroup = this.fieldsGroup();
        fg.patchValue(relative);
        this.relativeList.push(fg);
      }
    });

    this.investigation.valueChanges.subscribe(() => {
      this.validateInvestigation();
    });

    this.employeeOfEntity.valueChanges.subscribe(() => {
      this.validateEmployee();
    });

    this.contactWithEntity.valueChanges.subscribe(() => {
      this.validateContact();
    });

    this.relatives.valueChanges.subscribe(() => {
      this.validateRelatives();
    });

    this.legalRequirementsMet.valueChanges.subscribe(() => {
      this.setDate();
    });

    this.discTermsCheck.valueChanges.subscribe(() => {
      this.setTermDate();
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        this.bookmarkUrl.setValue("");
        if (event.payload.currentLink === '/acknowledgement') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitafDisclosureInfo((this.ackData.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitafDisclosureInfo((this.ackData.value), event);
          } else if (this.validate()) {
            this.submitafDisclosureInfo((this.ackData.value), event);
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/acknowledgement") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );

    this.eventBus.emit(new ProgressBarEvent({ id: 0 }));
  }

  innerHTMLClick(ev: Event) {

    let className: string = (ev.target as Element).className;
    if (className == 'af-appendix-a af-ponter') {
      let mainContentElement = document.querySelector(".appendixa");
      if (mainContentElement) {
        mainContentElement.scrollIntoView(true);
      }
    }

    if (className == 'af-appendix-b af-ponter') {
      let mainContentElement = document.querySelector(".appendixb");
      if (mainContentElement) {
        mainContentElement.scrollIntoView(true);
      }
    }

    if (className == 'af-annexure-1 af-ponter') {
      let mainContentElement = document.querySelector(".annexure1");
      if (mainContentElement) {
        mainContentElement.scrollIntoView(true);
      }
    }

    if (className == 'af-annexure-2 af-ponter') {
      let mainContentElement = document.querySelector(".annexure2");
      if (mainContentElement) {
        mainContentElement.scrollIntoView(true);
      }
    }
  }

  createRelative() {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.relativeList.push(fg);
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      relativeName: ['', [this.customValidator.validateRequiredField]],
      relationship: [''],
      relativeId: [''],
      orderNumber: ['']
    })
  }

  deleteClick(i) {
    this.relativeList.removeAt(i);
  }

  setLanguage() {
    this.ackRelatives = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_RELATIVES'];
    this.ackPendingInvest = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_PENDINGINVEST'];
    this.ackObaodDirector = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_OBAOD_DIRECTOR'];
    this.ackObaodCompensation = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_OBAOD_COMPENSATION'];
    this.ackEthnicity = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_ETHNICITY'];
    this.ackTerms = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_TERMS'];
    this.ackTerms16 = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_TERMS_16'];
    this.ackRevolvingDoor = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR'];
    this.ackRevolvingDoor1 = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR_1'];
    this.ackRevolvingDoor1A = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR_1A'];
    this.ackRevolvingDoor1B = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR_1B'];
    this.ackRevolvingDoor2 = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR_2'];
    this.ackRevolvingDoor2A = this.afDisclosureInfo.languageLookup['ACKNOWLDGMENT_REVOLVINGDOOR_2A'];
  }

  downloadAcknowledgmentPDF(): void {
    let fileName = "Acknowledgment of Consent";
    this.userService.downloadAcknowledgmentPDF(fileName, this.userService.getJobCountry(), this.userService.getJobSubCountry(), this.userService.getAfId()).subscribe({
      next: () => {
        this.termsCheck = true;
      },
      error: (error: Error) => {
        this.termsCheck = false;
        console.log("Error with downloading PDF: ", error);
      }
    });
  }

  submitafDisclosureInfo(afDisclosureInfo: AFDisclosure, event) {
    afDisclosureInfo.output = this.signatureImg;
    afDisclosureInfo.afId = this.userService.getAfId();
    afDisclosureInfo.legalRequirementCheckDate = this.date;
    afDisclosureInfo.discTermsCheckDate = this.termDate;
    afDisclosureInfo.discStatsCheckDate = this.termDate;

    this.consentService.saveAfDisclosure(afDisclosureInfo).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    let channelArray: Array<string> = this.nonRequiredFields();
    for (let controlKey in container.controls) {
      if (channelArray.some(x => x === controlKey)) {
        continue;
      }
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }
    this.getRelativeListErrorCount(errorCount, totalCount)
    return ((totalCount - errorCount) / totalCount) * 100;
  }

  getRelativeListErrorCount(errorCount: number, totalCount: number) {
    for (let container of this.ackData.get('relativeList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          let control = container.controls[controlKey];
          if (control.validator != null) {
            totalCount += container.controls[controlKey].validator.length;
            if (control.errors) {
              errorCount += Object.keys(control.errors).length;
            }
          }
        }
      }
    }
  }

  nonRequiredFields() {
    let channelArray: Array<string> = [];
    if ((this.employeeOfEntity.value == null || this.employeeOfEntity.value == 0) && (this.contactWithEntity.value == null || this.contactWithEntity.value == 0)) {
      channelArray = channelArray.concat(['entityDetails']);
    }

    if (this.investigation.value == null || this.investigation.value == 0) {
      channelArray = channelArray.concat(['investigationComments']);
    }

    return channelArray;
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.ackData.get('output').setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  termsOnBlur() {
    if (this.discTermsCheck.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  termsOnFocus() {
    this.check = false;
  }

  legalOnBlur() {
    if (this.legalRequirementsMet.value) {
      this.checkTwo = false;
    } else {
      this.checkTwo = true;
    }
  }

  legalOnFocus() {
    this.checkTwo = false;
  }

  dropDownScreenReaderRelative(relative: any): string {
    if (relative.controls.relationship.value) {
      return 'Selected ' + this.afDisclosureInfo.relationshipLookup[Object.keys(this.afDisclosureInfo.relationshipLookup).find(e => e === relative.controls.relationship.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderEth(): string {
    if (this.ackData.controls.race.value) {
      return 'Selected ' + this.raceLookup[Object.keys(this.raceLookup).find(e => e === this.ackData.controls.race.value)];
    } else {
      return 'blank';
    }
  }

  print() {
    let printElement = document.getElementById('print-section');
    let printContents = printElement.innerHTML;
    let popupWin = window.open("", "_blank", "top=0,left=0,height=auto,width=auto");

    popupWin.document.open();
    popupWin.document.write("\n      <html>\n        <head>\n          <title>" + ("") + "</title>        </head>\n        <body onload=\"window.print(); setTimeout(function(){ window.close(); }, 0)\">\n          " + printContents + "\n        </body>\n      </html>");
    popupWin.document.close();
  }

  setDate() {
    if (this.legalRequirementsMet.value) {
      if (!this.date) {
        if (!this.afDisclosureInfo.legalRequirementCheckDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.afDisclosureInfo.legalRequirementCheckDate) {
      this.date = null;
    }
  }

  setTermDate() {
    if (this.discTermsCheck.value) {
      if (!this.termDate) {
        if (!this.afDisclosureInfo.discTermsCheckDate) {
          this.termDate = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.afDisclosureInfo.discTermsCheckDate) {
      this.termDate = null;
    }
  }

  radioOnChange(event: MatRadioChange) {
    const radioGroupName = event.source.name;
    const radioGroupChgValue = event.value;
    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          radioGroup.groupValue = radioGroupChgValue;
          break;
        }
      }
    }
  }

  radioOnFocusOut(event: any) {
    const radioGroupName = event.target.name;
    let showError: boolean = false;
    if (this.radioButtonGroupData) {
      for (let radioGroup of this.radioButtonGroupData) {
        if (radioGroup.groupName == radioGroupName) {
          if (radioGroup.groupValue != "0" && radioGroup.groupValue != "1") {
            showError = true;
          }
          this.determineError(radioGroup.groupName, showError)
          break;
        }
      }
    }
  }

  determineError(groupName: string, showError: boolean) {
    switch (groupName) {
      case "radioRelatives": {
        this.showRadioRelatives = showError;
        break;
      }
      case "radioInvestigation": {
        this.showRadioInvestigation = showError;
        break;
      }
      case "radioOutsideBusinessQuestion1": {
        this.showRadioOutsideBusinessQuestion1 = showError;
        break;
      }
      case "radioEmployedCompensated": {
        this.showRadioEmployedCompensated = showError;
        break;
      }
      case "radioEmployeeOfEntity": {
        this.showRadioEmployeeOfEntity = showError;
        break;
      }
      default: {
        this.showRadioContactWithEntity = showError;
        break;
      }
    }
  }

  radioOnFocus(event: any) {
    this.determineError(event.target.name, false)
  }

  @HostListener('window:resize', ['$event'])
  @HostListener('window:load', ['$event'])
  handleZoom() {
    const windowWidth = window.innerWidth;
    if (windowWidth <= AF_CONSTANTS.windowWidth) {
      this.relativeListChange = true;
    } else {
      this.relativeListChange = false;
    }
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  idAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    let keyA: number = Number(a.key);
    let keyB: number = Number(b.key);

    if (keyA > keyB) {
      return 1;
    }
    if (keyA < keyB) {
      return -1;
    }
    return 0;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  validate() {
    this.ackData.markAllAsTouched();
    this.userService.setFocus(this.ackData);
    this.hasBeenSubmitted = true;

    if (!this.legalRequirementsMet.value) {
      this.legalRequirementsMet.setErrors({ invalid: true });
      this.checkTwo = true;
    }

    if (!this.discTermsCheck.value) {
      this.discTermsCheck.setErrors({ invalid: true });
      this.check = true;
    }

    if (!this.output.value) {
      this.output.setErrors({ invalid: true });
    }

    if (!this.termsCheck) {
      this.pdfButtonFocus();

      return false;
    }

    if (this.ackData.invalid) {
      return false;
    }

    return true;
  }

  pdfButtonFocus(): void {
    setTimeout(() => {
      this.pdfButton.nativeElement.focus();
    });
  }

  validateInvestigation() {
    if (this.investigation.value == null || this.investigation.value == 0) { // If the user selects No or hasn't selected for investigation radio button
      this.ackData.get('investigationComments').setValidators(null);
      this.ackData.get('investigationComments').setErrors(null);
    } else {
      this.ackData.get('investigationComments').setValidators([Validators.required, this.customValidator.validateRequiredField]);
    }
    this.ackData.get('investigationComments').updateValueAndValidity();
  }

  validateEmployee() {
    if (this.employeeOfEntity.value == 0) { //If the user selects No for revolving door first radio button
      this.contactWithEntity.setValidators(null);
      this.contactWithEntity.setErrors(null);
      this.contactWithEntity.setValue(null);
      this.validateContact();
      this.ackData.get('positionInEntity').setValidators(null);
      this.ackData.get('positionInEntity').setErrors(null);
      this.ackData.get('nameOfEntity').setValidators(null);
      this.ackData.get('nameOfEntity').setErrors(null);
      this.ackData.get('entityDetails').setValidators(null);
      this.ackData.get('entityDetails').setErrors(null);
    } else {
      if (this.employeeOfEntity.value == null || this.employeeOfEntity.value == -1) {
        this.contactWithEntity.setValidators(null);
        this.contactWithEntity.setErrors(null);
        this.contactWithEntity.setValue(null);
        this.validateContact();
        this.ackData.get('positionInEntity').setValidators(null);
        this.ackData.get('positionInEntity').setErrors(null);
        this.ackData.get('nameOfEntity').setValidators(null);
        this.ackData.get('nameOfEntity').setErrors(null);
        this.ackData.get('entityDetails').setValidators(null);
        this.ackData.get('entityDetails').setErrors(null);
      } else {
        this.contactWithEntity.setValidators([Validators.required]);
        this.ackData.get('positionInEntity').setValidators([Validators.required, this.customValidator.validateRequiredField]);
        this.ackData.get('nameOfEntity').setValidators([Validators.required, this.customValidator.validateRequiredField]);
        this.ackData.get('entityDetails').setValidators([Validators.required, this.customValidator.validateRequiredField]);
      }
    }
    this.contactWithEntity.updateValueAndValidity();
    this.ackData.get('positionInEntity').updateValueAndValidity();
    this.ackData.get('nameOfEntity').updateValueAndValidity();
    this.ackData.get('entityDetails').updateValueAndValidity();
  }

  validateContact() {
    if (this.contactWithEntity.value == 0) { //If the user selects No for revolving door second radio button
      this.ackData.get('entityDetails').setValidators(null);
      this.ackData.get('entityDetails').setErrors(null);
    } else {
      if ((this.employeeOfEntity.value == 0 && this.contactWithEntity.value == null) || this.contactWithEntity.value == null) {
        this.ackData.get('entityDetails').setValidators(null);
        this.ackData.get('entityDetails').setErrors(null);
      } else {
        this.ackData.get('entityDetails').setValidators([Validators.required, this.customValidator.validateRequiredField]);
      }
    }
    this.ackData.get('entityDetails').updateValueAndValidity();
  }

  validateRelatives() {
    if (this.relatives.value != null) {
      for (let container of this.ackData.get('relativeList')['controls']) {
        for (let controlKey in container.controls) {
          if (container.controls.hasOwnProperty(controlKey) && (controlKey == "relativeName" || controlKey == "relationship")) {
            this.validateRelative(container, controlKey)
          }
        }
      }
    }
  }

  validateRelative(container: any, controlKey: string) {
    let control = container.controls[controlKey];
    if (this.relatives.value == 0) { //If the user selects No for Relatives radio button
      control.setValidators(null);
      control.setErrors(null);
    } else {
      control.setValidators([Validators.required, this.customValidator.validateRequiredField]);
    }
    control.updateValueAndValidity();
  }
}

export class KeyValDomain {
  keyValue: KeyVal[] = [];

  add(keyValue: KeyVal) {
    this.keyValue.push(keyValue);
  }

  get(): KeyVal[] {
    return this.keyValue;
  }
}
